/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect } from 'react'
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from '../../components/ConfirmationModal'
import DebugAccordion from '../../components/DebugAccordion'
import Icon from '../../components/Icon.jsx'
import Loadable from '../../components/Loadable'
import LoadingAnimation from '../../components/LoadingAnimation'
import PageTitle from '../../components/PageTitle.jsx'
import ProfileNav from './components/ProfileNav'
import ProfileTools from './components/ProfileTools.jsx'

import { NotificationTypes, addNotification } from '../../state/actions/notificationActions'
import { fetchProfile, updateProfile } from '../../state/actions/profileActions'
import { fetchProfileSchema } from '../../state/actions/profileSchemaActions'
import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions'

import { profileSignal, profileLoadedSignal, profileCcSchemaSignal, profileErrorSignal } from '../../state/signals/profileSignals'
import { profileSchemaSignal, profileSchemaLoadedSignal, profileSchemaVersionSignal, profileSchemaErrorSignal } from '../../state/signals/profileSchemaSignals'
import { schemaManifestSignal } from '../../state/signals/schemaManifestSignals'

import { ComponentInterfaceType } from '../../utils/constants'
import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'

export default function ProfileApiInterfaceList() {

    const navigate = useNavigate()
    const { beId, profileId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/profiles`)
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewProfiles) {
            addNotification('You are not authorized to view UTG profiles for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditProfiles
    })

    const apiInterfacesComputed = useComputed(() => {
        if (profileSignal.value) {
            // extract apiInterface from profile for ease of use
            const interfaces = profileSignal.value?.config?.apiInterfaceConfig?.apiInterface || []
            return interfaces
        }
        return []
    })

    useEffect(() => {
        if (beIdChanged) return
        fetchProfile(beId, profileId, config, accessToken).then(
            () => {
                fetchSchemaManifest().then(
                    () => {
                        fetchProfileSchema(beId, profileCcSchemaSignal.value, schemaManifestSignal.value, config, accessToken)
                    }
                )
            }
        )
    }, [beId, beIdChanged, profileId, config, accessToken])

    const apiInterfaceDropdownItemsComputed = useComputed(() => {
        if (profileSchemaSignal.value?.properties) {
            const apiInterfaceItems = profileSchemaSignal.value?.properties?.config?.properties?.apiInterfaceConfig?.properties?.apiInterface?.items ?? []
            const apiInterfaceOptions = apiInterfaceItems.map(s => {
                const apiInterfaceName = s.properties.cdType["s4-caption"]
                const id = s.properties.cdType["s4-model-key"]
                return {
                    apiInterfaceName,
                    id
                }
            })
            return apiInterfaceOptions
        }
        return []
    })
 

    const showDeleteModalSignal = useSignal(false)
    const confirmCdTypeSignal = useSignal('')
    const confirmTaskDescSignal = useSignal('')
    const confirmListenPortSignal = useSignal('')
    const accordionData = [
        { name: 'API Interfaces', data: apiInterfacesComputed.value },
        { name: 'Component Profile', data: profileSignal.value },
        { name: 'API Interfaces Dropdown data', data: apiInterfaceDropdownItemsComputed.value },
        { name: 'Profile Schema', data: profileSchemaSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: schemaManifestSignal.value }
    ]

    function ApiInterfaceTableRows({ apiInterfaces }) {
        let rows = []
        apiInterfaces.forEach(apiInterface => {
            rows.push(
                <ApiInterfaceRow apiInterface={apiInterface} key={`${apiInterface.cdType}${apiInterface.listenPort}`} />
            )
        })

        return (
            <tbody>{rows}</tbody>
        )
    }

    function ApiInterfaceRow({ apiInterface }) {
        const cdType = apiInterface.cdType
        const taskDesc = apiInterface.taskDesc
        const listenPort = apiInterface.listenPort
        return (
            <tr>
                <td><Link to={`./${apiInterface.cdType}?port=${apiInterface.listenPort}`}>{apiInterface.taskDesc}</Link></td>
                <td>{apiInterface.cdType}</td>
                <td>{apiInterface.listenPort}</td>
                <td>
                    {canEditComputed.value && <Link to={`./${apiInterface.cdType}?port=${apiInterface.listenPort}`}><Button variant="outline-primary"><Icon icon="pencil" width="16" height="16"></Icon> Edit</Button></Link>}
                    {canEditComputed.value && <Button className="mx-2" variant="outline-danger" onClick={() => showModal(cdType, taskDesc, listenPort)}><Icon icon="trash" width="16" height="16"></Icon> Delete</Button>}
                </td>
            </tr>
        )
    }

    const deleteApiInterface = () => {
        showDeleteModalSignal.value = false

        // filter out apiInterface to be deleted
        const filteredApiInterfaces = profileSignal.value.config.apiInterfaceConfig.apiInterface.filter(apiInterface =>
            !(apiInterface.listenPort == confirmListenPortSignal.value));

        // set newly filtered apiInterface array 
        profileSignal.value.config.apiInterfaceConfig.apiInterface = filteredApiInterfaces

        let data = {
            id: profileSignal.value.id,
            name: profileSignal.value.name,
            businessEntityId: profileSignal.value.businessEntityId,
            config: profileSignal.value.config,
        }
                
        // add a schema value to the config data. This allows us to load this specific schema rather than a default one in future edits
        data.config.utgGlobal.ccschema = profileSchemaVersionSignal.value

        updateProfile(beId, profileId, data, config, accessToken, true)
            .then(() => {
                addNotification('The Api Interface was successfully removed.', NotificationTypes.SUCCESS, true)
            })
            .catch(error => {
                console.log(error.toString())
                addNotification(error.toString(), NotificationTypes.ERROR, false)
                return false
            })
            .finally(() => {
                //isFormSubmittingSignal.value = false
                // must return false to avoid postback
                return false
            })
    }

    const showModal = (cdType, taskDesc, listenPort) => {
        // just set the state and the modal will appear
        confirmCdTypeSignal.value = cdType
        confirmTaskDescSignal.value = taskDesc
        confirmListenPortSignal.value = listenPort
        showDeleteModalSignal.value = true
    }

    function NewApiInterfaceDropdown() {
        // only allow one Oracle Interface per UTG
        let oracleApiInterfaces = apiInterfacesComputed.value.filter(apiInterface => {
            return apiInterface.cdType == ComponentInterfaceType.OraclePaymentInterface
        })

        let apiInterfaceOptions = apiInterfaceDropdownItemsComputed.value.map(s => {
            let isDisabled = false

            if (s.id == ComponentInterfaceType.OraclePaymentInterface && oracleApiInterfaces.length) {
                isDisabled = true
            }

            return (
                <Dropdown.Item href={`/be/${beId}/profiles/${profileId}/apiinterfaces/${s.id}`} key={s.id} disabled={isDisabled}>{s.apiInterfaceName}</Dropdown.Item>
            )
        })

        return (
            <DropdownButton id="dropdown-add-apiinterface"
                variant="outline-secondary"
                title="New API Interface"
                className="mx-3">
                {apiInterfaceOptions}
            </DropdownButton>
        )
    }

    return (
        <>
            <ConfirmationModal
                show={showDeleteModalSignal.value}
                handleCancel={
                    () => showDeleteModalSignal.value = false
                }
                handleConfirm={
                    () => deleteApiInterface()
                }
                message={`Are you sure you want to delete ${confirmTaskDescSignal.value}? This action cannot be undone.`}
                title={`Deleting ${confirmTaskDescSignal.value}`}
            />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                    Profile List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{
                    profileLoadedSignal.value ? (<>Manage {profileSignal.value.name}</>) : (<>Loading...</>)
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title='Profile API Interfaces' canEditSignal={canEditComputed} />
                <Loadable loadedSignals={[profileLoadedSignal]} errorSignals={[profileErrorSignal]}>
                    <ProfileTools profile={profileSignal.value}></ProfileTools>
                </Loadable>
            </Stack>
            <ProfileNav activeKey="2"></ProfileNav>

            <Table responsive striped hover size="sm">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th>Type</th>
                        <th>Port</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <ApiInterfaceTableRows apiInterfaces={apiInterfacesComputed.value} />
            </Table>
            {
                canEditComputed.value && <NewApiInterfaceDropdown />
            }
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}